import { useMemo } from "react";
import styled from "styled-components";
import {
  ArrowTopRightOnSquareIcon,
  PlusCircleIcon,
} from "@heroicons/react/24/outline";
import { Button, Characteristic } from "@sablier/v2-components/molecules";
import { ActionCategory } from "@sablier/v2-constants";
import { useChainExplorer } from "@sablier/v2-hooks";
import { _ } from "@sablier/v2-mixins";
import type { Action } from "@sablier/v2-models";
import type { ComponentProps } from "react";

type Description = ComponentProps<typeof Characteristic>["description"];

interface Props {
  action: Action;
  description: Description;
  icon: typeof PlusCircleIcon;
  title: string;
}

const WrapperPartial = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 1);
    align-items: flex-start;
    width: 100%;
    padding-top: calc(${(props) => props.theme.sizes.edge} * 1);
    padding-bottom: calc(${(props) => props.theme.sizes.edge} * 1);
    overflow-x: hidden;
    &:not(:last-child) {
      border-bottom: 2px solid ${(props) => props.theme.colors.border};
    }
  }
`;

const Top = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    position: relative;
    width: 100%;
    column-gap: ${(props) => props.theme.sizes.edge};
    & > *:first-child {
      margin-right: auto;
    }
  }
`;

const Moment = styled.div`
  position: absolute;
  top: -15px;
  right: -15px;
  min-width: 200px;
  padding: 15px;
  text-align: right;

  > *:first-child {
    user-select: none;
  }
  > *:last-child {
    display: none;
  }

  > p {
    ${(props) => props.theme.styles.textButtonMini}
    & {
      color: ${(props) => props.theme.colors.gray400};
    }
  }

  :hover {
    > *:first-child {
      display: none;
    }
    > *:last-child {
      display: inherit;
    }
  }
`;

const Actions = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    /* margin-left: auto; */
    & > *:first-child {
      border-top-right-radius: 0px;
      border-bottom-right-radius: 0px;
    }

    & > *:nth-child(2) {
      border-top-left-radius: 0px;
      border-bottom-left-radius: 0px;
    }
  }
`;

const Bottom = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    row-gap: ${(props) => props.theme.sizes.edge};
    width: 100%;
    padding-left: 2px;
    p,
    span {
      color: ${(props) => props.theme.colors.gray200} !important;
    }
  }
`;

const Wrapper = styled(WrapperPartial)`
  ${(props) => props.theme.medias.maxMD} {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 3 / 4);

    ${Top} {
      flex-direction: column;
      row-gap: ${(props) => props.theme.sizes.edge};
      align-items: flex-start;
    }
    ${Moment} {
      position: relative;
      top: inherit;
      right: inherit;
      padding: 0;
      padding-left: 2px;
      text-align: left;
    }
  }
`;

function Template({ action, description, icon, title }: Props) {
  const moment = useMemo(
    () => _.toDuration(action.timestamp, "date-full")[0],
    [action],
  );

  const distance = useMemo(
    () => _.toDayjs(action.timestamp).fromNow(),
    [action],
  );

  const explorer = useChainExplorer({
    chainId: action.chainId,
    type: "tx",
    hash: action.hash,
  });

  return (
    <Wrapper>
      <Top>
        <Actions>
          <Button
            accent={
              action.category === ActionCategory.CANCEL
                ? "failLight"
                : "dark200"
            }
            appearance={"solid"}
            isPreview
            isMini
            left={icon}
            purpose={"external"}
            title={title}
          />
          <Button
            accent={
              action.category === ActionCategory.CANCEL ? "fail300" : "dark200"
            }
            appearance={"solid"}
            isMini
            purpose={"external"}
            left={ArrowTopRightOnSquareIcon}
            to={explorer}
          />
        </Actions>
        <Moment>
          <p>{moment}</p>
          <p>{distance}</p>
        </Moment>
      </Top>
      <Bottom>
        <Characteristic description={description} />
      </Bottom>
    </Wrapper>
  );
}

export default Template;
